// ============================================================
// Viewer/预览数据链路（DW-P0-5）：设备 WS + 绑定回填 + 视觉规则 + 数据绑定
// 编辑器/预览/发布/Viewer 同源；修复「预览页只渲染画面、设备动画与构件数据静默」
// ============================================================

import { useEffect } from 'react';
import { useBindingApply } from './useBindingApply';
import { useVisualBindingApply } from './useVisualBindingApply';
import { ensureDeviceSocket } from '../services/deviceSocket';
import { DEVICE_LINK_TYPE, useSceneBindingStore } from '../stores/sceneBindingStore';
import useSceneStore from '../stores/sceneStore';
import { useDataBindingStore } from '../stores/dataBindingStore';

/** 从场景 config 恢复数据源与属性绑定（编辑器/预览/发布/Viewer 同源） */
export function initDataBindingsFromConfig(cfg: Record<string, unknown> | null | undefined): void {
  if (!cfg) return;
  if (cfg.datasources || cfg.bindings) {
    useDataBindingStore.getState().initDocument({
      datasources: (cfg.datasources as unknown[]) ?? [],
      nodes: Object.entries((cfg.bindings as Record<string, unknown[]>) ?? {}).map(([nodeId, list]) => ({
        id: nodeId,
        bindings: list,
      })),
    } as never);
  }
}

export interface ViewerDataLinkOptions {
  /** 匿名查看器令牌（云端 meta.wsToken；缺省沿用登录 token） */
  socketToken?: string;
  /** 快照模式：绑定已随快照写入 store，跳过 API 拉取 */
  bindingsLoadedExternally?: boolean;
}

/** 设备实时订阅 + 视觉规则应用 + 数据绑定应用 + device-link 回填（幂等） */
export function useViewerDataLink(sceneId?: string | null, opts: ViewerDataLinkOptions = {}): void {
  useBindingApply();
  useVisualBindingApply();

  useEffect(() => {
    ensureDeviceSocket(opts.socketToken);
  }, [opts.socketToken]);

  useEffect(() => {
    if (!sceneId || opts.bindingsLoadedExternally) return;
    let cancelled = false;
    useSceneBindingStore
      .getState()
      .load(sceneId)
      .then(() => {
        if (cancelled) return;
        const links = useSceneBindingStore
          .getState()
          .bindings.filter((b) => b.visualType === DEVICE_LINK_TYPE && b.deviceId);
        if (links.length === 0) return;
        useSceneStore.getState().setModels(
          useSceneStore.getState().models.map((m) => {
            const link = links.find((l) => l.modelNodeId === m.id);
            return link?.deviceId ? { ...m, deviceId: link.deviceId } : m;
          }),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [sceneId, opts.bindingsLoadedExternally]);
}
